// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------
import ShippingOfferDal from './../../../dal/ShippingOffer';
import { put, call, select } from 'redux-saga/effects';
import {
    actionStatusStarted,
    actionStatusFailed,
    actionStatusSuccess,
} from './../../../store/actionStatus';
import {
    GET_SHIPPING_ACCESSORY_OFFER_DATA,
    GET_SHIPPING_DEVICE_OFFER_DATA,
    getShippingDeviceOfferDataSuccess,
    getShippingAccessoryOfferDataSuccess,
} from './../../../store/orderCreation';

import { deviceSelector } from './../../../store/deviceInfo/selectors';

import {
    zipCodeSelector,
    orderCreationSelector,
    isSoftwareOrderSelector,
} from './../../../store/orderCreation/selectors';

import { selectedCountryInfoSelector } from './../../../store/countries';
import { userInfoSelector } from './../../../store/user/selector';
import i18n from 'i18next';

export function* getShippingDeviceOffer() {
    try {
        yield put(
            actionStatusStarted({ actionName: GET_SHIPPING_DEVICE_OFFER_DATA })
        );

        const orderInfo: any = yield select(state =>
            orderCreationSelector(state)
        );
        
        const agentInfo: any = yield select(state => userInfoSelector(state));

        const isSoftwareOffer: boolean = yield select(state => isSoftwareOrderSelector(state));

        const payload = getOfferInfos(orderInfo);

        const postalCode = yield select(state => zipCodeSelector(state));

        const { countryCode } = yield select(state =>
            selectedCountryInfoSelector(state)
        );

        const agentTier = agentInfo.tierName;

        const language = i18n.language ?? 'en-US';
        
        const request = {
            countryCode,
            postalCode,
            agentTier,
            isSoftwareOffer,
            payload: payload.map(({ serialNumber, symptomCode, offerSku }) => ({
                serialNumber,
                symptomCode,
                offerSku,
            })),
            language
        };
        const response = yield call(
            ShippingOfferDal.getDeviceShippingOffers,
            request
        );

        const shippingOffers = transformToShippingOfferStoreData(
            payload,
            response
        );

        yield put(getShippingDeviceOfferDataSuccess(shippingOffers));

        yield put(
            actionStatusSuccess({
                actionName: GET_SHIPPING_DEVICE_OFFER_DATA,
            })
        );
    } catch (e) {
        yield yield put(
            actionStatusFailed({
                actionName: GET_SHIPPING_DEVICE_OFFER_DATA,
                error: e,
            })
        );
    }
}

export function* getShippingAccessoriesOffer() {
    try {
        yield put(
            actionStatusStarted({
                actionName: GET_SHIPPING_ACCESSORY_OFFER_DATA,
            })
        );

        const orderInfo: any = yield select(state =>
            orderCreationSelector(state)
        );

        const agentInfo: any = yield select(state => userInfoSelector(state));

        const deviceInfo = yield select(state => deviceSelector(state));

        const payload = getAccessoryOffersInfo(
            orderInfo,
            deviceInfo.serialNumber
        );

        const postalCode = yield select(state => zipCodeSelector(state));

        const { countryCode } = yield select(state =>
            selectedCountryInfoSelector(state)
        );

        const agentTier = agentInfo.tierName;

        const language = i18n.language ?? 'en-US';

        const request = {
            countryCode,
            postalCode,
            agentTier,
            payload: payload.map(({ serialNumber, symptomCode, offerSku }) => ({
                serialNumber,
                offerSku,
                accessorySku: symptomCode,
            })),
            language
        };

        const response = yield call(
            ShippingOfferDal.getAccessoryShippingOffers,
            request
        );

        yield put(
            getShippingAccessoryOfferDataSuccess(
                transformToShippingOfferStoreData(payload, response)
            )
        );

        yield put(
            actionStatusSuccess({
                actionName: GET_SHIPPING_ACCESSORY_OFFER_DATA,
            })
        );
    } catch (e) {
        yield yield put(
            actionStatusFailed({
                actionName: GET_SHIPPING_ACCESSORY_OFFER_DATA,
                error: e,
            })
        );
    }
}

function getOfferInfos(orderInfo: any) {
    const repairItems = orderInfo && orderInfo.repairItems;
    const isFullDeviceRepair = repairItems && repairItems.isFullDeviceRepair;
    const isSoftwareOffer = repairItems && repairItems.isSoftwareOrder;
    return Object.keys(repairItems && repairItems.deviceItems || {}).reduce(
        (payLoad: any, sku: any): any[] => {
            const deviceRepairInfo = repairItems.deviceItems[sku];
            const symptomCode = (isFullDeviceRepair || isSoftwareOffer)
                ? deviceRepairInfo && deviceRepairInfo.problemType && deviceRepairInfo.problemType.symptomCode
                : deviceRepairInfo && deviceRepairInfo.symptomCode;
            const selectedRepairOffer = orderInfo && orderInfo.selectedRepairOffers && orderInfo.selectedRepairOffers[symptomCode];

            if (!selectedRepairOffer || !Object.keys(selectedRepairOffer).length) {
                return payLoad;
            }

            const title = isFullDeviceRepair ? getFullDeviceTitle(orderInfo, deviceRepairInfo.name) : deviceRepairInfo.name;
            const serialNumber = isFullDeviceRepair
                ? deviceRepairInfo && deviceRepairInfo.serialNumber
                : orderInfo.selection.parent.serialNumber;
            const offerSku = selectedRepairOffer.offerId;

            return [...payLoad, { serialNumber, symptomCode, offerSku, title }];
        },
        []
    );
}

function getFullDeviceTitle(orderInfo: any, defaultTitle: string) {
    if (orderInfo) {
        const { repairOffer } = orderInfo;

        if (repairOffer) {
            const { fullDeviceRepairOffer } = repairOffer;

            if (fullDeviceRepairOffer) {
                const { offerProperties } = fullDeviceRepairOffer;

                if (offerProperties) {
                    const { brandName, productSeries } = offerProperties;

                    if (brandName && productSeries) {
                        return `${brandName} ${productSeries}`;
                    }
                }
            }
        }
    }
    return defaultTitle;
}

export function getAccessoryOffersInfo(orderInfo: any, serialNumber: string) {
    const repairItems = orderInfo && orderInfo.repairItems;
    const repairItemAccessories = repairItems && repairItems.accessories || {};
    return Object.keys(repairItemAccessories).reduce(
        (payLoad: any, sku: any): any[] => {
            const accessoryRepairInfo = repairItemAccessories[sku];
            const accessoryRepairInfoData = accessoryRepairInfo && accessoryRepairInfo.data || {};
            const title = accessoryRepairInfoData.name;
            const symptomCode = accessoryRepairInfoData.symptomCode;
            const selectedRepairOffer = orderInfo && orderInfo.selectedRepairOffers && orderInfo.selectedRepairOffers[symptomCode];

            if (!selectedRepairOffer || !Object.keys(selectedRepairOffer).length) {
                return payLoad;
            }

            const offerSku = selectedRepairOffer.offerId;

            return [...payLoad, { serialNumber, symptomCode, offerSku, title }];
        },
        []
    );
}

function transformToShippingOfferStoreData(payload: any[], response: any) {
    let shippingOffers: any[] = [];

    for (let offerCounter = 0; offerCounter < payload.length; offerCounter++) {
        const currentPayload = payload[offerCounter];

        let shippingOffer = {
            title: currentPayload.title,
            symptomCode: currentPayload.symptomCode,
            offerSku: currentPayload.offerSku,
            offers: [],
        };
        const correspondingShippingOffers = response[offerCounter].offers;
        let offerForSku: any = [];
        for (
            let shippingOfferCounter = 0;
            shippingOfferCounter < correspondingShippingOffers.length;
            shippingOfferCounter++
        ) {
            offerForSku = [
                ...offerForSku,
                {
                    ...correspondingShippingOffers[shippingOfferCounter],
                    listPrice:
                        correspondingShippingOffers[shippingOfferCounter]
                            .price || 0,
                    offerSku:
                        correspondingShippingOffers[shippingOfferCounter]
                            .offerId,
                },
            ];
        }

        shippingOffer = { ...shippingOffer, offers: offerForSku };

        shippingOffers = [...shippingOffers, shippingOffer];
    }

    return shippingOffers;
}
